import type { ListBuilderMobileCardConfig, SelectedObject } from '../../types';

/**
 * Shared pieces of the three cart list configurations (PD-13666): the fields
 * every cart row carries, the objects the field picker draws on, and the
 * mobile card layout.
 */

/**
 * The fields the cart endpoint delivers on every line. These are already on
 * the row, so the hydrator treats them as delivered and leaves them alone —
 * they are listed because a list's `requiredFields` is also what the CSV
 * export and the column gating read.
 */
export const CART_REQUIRED_FIELDS = [
	'_id',
	'_fk_item__id',
	'lineNumber',
	'primaryImageUrl',
	'itemDescription',
	'itemManufacturer',
	'itemNDC',
	'itemNumber',
	'itemExternalId',
	'itemExternalId2',
	'quantityOrdered',
	'price',
	'total',
	'subtotal',
	'minimumPrice',
	'perOrderLimit',
	'totalStock',
	'soonestExpirationDate',
	'isShortDate',
	'itemIsActive',
];

/**
 * `item` fields the picker offers but the cart endpoint does NOT return.
 * Declaring the `item` object with a `rowIdField` is what lets the hydrator
 * load these by the line's item id, so switching one on for a tenant is a
 * configuration change with no endpoint work.
 *
 * This is where PD-13667's Pharmsource request lands: `sizeCount` +
 * `unitOfMeasure` for item size, `externalId` / `externalId2` for SKU, and the
 * cost fields below.
 *
 * The cost fields are gated by `price_contracts:costs:view` through the shared
 * field-permission rules (`libs/shared/src/utils/item-cost.ts`), so they are
 * omitted from the picker and never selected for a user without it.
 * `averageNetCost` is gated more strictly, by `items:net_cost:view`.
 */
const CART_ITEM_PICKER_FIELDS = [
	{ fieldName: '_id', fieldLabel: 'Item ID' },
	{ fieldName: 'sizeCount', fieldLabel: 'Size/Count' },
	{ fieldName: 'unitOfMeasure', fieldLabel: 'Unit of Measure' },
	{ fieldName: 'strength', fieldLabel: 'Strength' },
	{ fieldName: 'externalId', fieldLabel: 'External Id' },
	{ fieldName: 'externalId2', fieldLabel: 'External Id 2' },
	{ fieldName: 'category', fieldLabel: 'Category' },
	{ fieldName: 'subcategory', fieldLabel: 'Subcategory' },
	{ fieldName: 'ndc', fieldLabel: 'NDC' },
	{ fieldName: 'standardNdc', fieldLabel: 'Standard NDC' },
	{ fieldName: 'upc', fieldLabel: 'UPC' },
	{ fieldName: 'caseSize', fieldLabel: 'Case Size' },
	{ fieldName: 'quantityOnOrder', fieldLabel: 'Qty On Order' },
	{ fieldName: 'quantityOnBackorder', fieldLabel: 'Qty On Backorder' },
	{ fieldName: 'dailyLimit', fieldLabel: 'Daily Limit' },
	{ fieldName: 'monthlyLimit', fieldLabel: 'Monthly Limit' },
	{ fieldName: 'manufacturerItemId', fieldLabel: 'Manufacturer Item ID' },
	{ fieldName: 'brandEquivalent', fieldLabel: 'Brand Equivalent' },
	{ fieldName: 'countryOfOrigin', fieldLabel: 'Country of Origin' },
	{ fieldName: 'storageType', fieldLabel: 'Storage Type' },
	{ fieldName: 'weight', fieldLabel: 'Weight' },
	{ fieldName: 'isHazmat', fieldLabel: 'Is Hazmat' },
	{ fieldName: 'isRefrigerated', fieldLabel: 'Is Refrigerated' },
	{ fieldName: 'isShortDate', fieldLabel: 'Is Short Date' },
	{ fieldName: 'averageWholesalePrice', fieldLabel: 'Avg Wholesale Price' },
	// Cost fields — see the permission note above.
	{ fieldName: 'acquisitionCost', fieldLabel: 'Acquisition Cost' },
	{ fieldName: 'sellCost', fieldLabel: 'Sell Cost' },
	{ fieldName: 'averageCost', fieldLabel: 'Average Cost' },
	{ fieldName: 'averageNetCost', fieldLabel: 'Average Net Cost' },
];

/** Values the cart endpoint flattens onto each order_detail row. */
const CART_ORDER_DETAIL_PICKER_FIELDS = [
	{ fieldName: '_id', fieldLabel: 'ID' },
	{ fieldName: 'lineNumber', fieldLabel: 'Line #' },
	{ fieldName: 'itemDescription', fieldLabel: 'Item' },
	{ fieldName: 'itemManufacturer', fieldLabel: 'Manufacturer' },
	{ fieldName: 'itemNDC', fieldLabel: 'NDC' },
	{ fieldName: 'itemNumber', fieldLabel: 'Item #' },
	{ fieldName: 'quantityOrdered', fieldLabel: 'Qty' },
	{ fieldName: 'quantityShipped', fieldLabel: 'Qty Shipped' },
	{ fieldName: 'quantityBackordered', fieldLabel: 'Qty Backordered' },
	{ fieldName: 'price', fieldLabel: 'Price' },
	{ fieldName: 'minimumPrice', fieldLabel: 'Minimum Price' },
	{ fieldName: 'subtotal', fieldLabel: 'Subtotal' },
	{ fieldName: 'discount', fieldLabel: 'Discount' },
	{ fieldName: 'tax', fieldLabel: 'Tax' },
	{ fieldName: 'freight', fieldLabel: 'Freight' },
	{ fieldName: 'total', fieldLabel: 'Total' },
	{ fieldName: 'totalStock', fieldLabel: 'On Hand' },
	{ fieldName: 'soonestExpirationDate', fieldLabel: 'Next Exp' },
	{ fieldName: 'perOrderLimit', fieldLabel: 'Per Order Limit' },
	{ fieldName: 'primaryImageUrl', fieldLabel: 'Image' },
];

/**
 * The objects a cart list draws on.
 *
 * `order_detail` carries the row itself; `item` is declared with the line's
 * item FK as its `rowIdField` so the hydrator can fill in any item column a
 * tenant configures. Quote custom lines have no item FK, so they simply
 * contribute no id and their item-derived cells stay empty — which is why
 * those lines are badged (see `cart-item-badges`).
 */
export const createCartObjects = (): Array<SelectedObject> => [
	{
		objectTable: 'order_detail',
		objectLabel: 'Cart Line',
		rowIdField: '_id',
		selectedFields: CART_ORDER_DETAIL_PICKER_FIELDS,
	},
	{
		objectTable: 'item',
		objectLabel: 'Item',
		baseUrl: '/items/:id',
		rowIdField: '_fk_item__id',
		selectedFields: CART_ITEM_PICKER_FIELDS,
	},
];

/**
 * Mobile card layout for a cart line, mirroring the shop's zones config
 * (`SHOP_MOBILE_CARD_CONFIG`): identity in the title block, the line total as
 * the hero metric, and the editable controls as the full-width controls row at
 * the bottom of the card so quantity, price and remove stay reachable without
 * horizontal scrolling.
 *
 * The image is hidden for the same reason the shop hides it — the card leads
 * with the item name, and a thumbnail costs a row of height on a surface where
 * a rep is scrolling many lines.
 */
export const CART_MOBILE_CARD_CONFIG: ListBuilderMobileCardConfig = {
	cardLayout: 'zones',
	primaryFields: ['item_details'],
	heroField: 'total',
	controlsColumns: ['quantity', 'price', 'actions'],
	// The image costs a row of height the card cannot spare, and the card
	// leads with the item name anyway — the same trim the shop card makes.
	hiddenFields: ['image'],
};
